import { Component, type ReactNode } from 'react';
import { czyBladWczytaniaModulu, czyMoznaPrzeladowac, przeladujPoBledzieModulu } from '@/lib/samonaprawaWdrozenia';

interface Props {
  children: ReactNode;
  /** Rendered in place of the children once a descendant throws. */
  fallback: ReactNode;
  /** Changing this value clears a captured error (e.g. pass the route path so
   *  navigating away recovers automatically). */
  resetKey?: string;
  /** Short label included in the console log so crashes are attributable. */
  label?: string;
}

interface State {
  hasError: boolean;
}

/**
 * Generic render-error boundary. Catches a crash in its subtree and shows
 * `fallback` instead of letting the error unmount everything above it (which
 * leaves a blank screen). The error is logged rather than swallowed, and the
 * boundary recovers when `resetKey` changes.
 *
 * Scope it tightly around independent regions (e.g. the header) so one region's
 * crash never takes down the rest of the app.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: unknown) {
    const tag = this.props.label ? `:${this.props.label}` : '';
    // Keep the crash visible in the console for diagnosis rather than swallowing it.
    console.error(`[ErrorBoundary${tag}] subtree crashed:`, error, info);

    /**
     * STARA KARTA PO WDROŻENIU NAPRAWIA SIĘ SAMA (zgłoszenie Piotra 27.09).
     *
     * Gdy przyczyną jest brak leniwego kawałka aplikacji — bo wdrożenie zostawiło tylko nowe
     * pliki — zasłona jest złą odpowiedzią: nic nie jest zepsute, człowiek ma po prostu w
     * karcie aplikację sprzed kilku wdrożeń. Przeładowujemy RAZ; regułę „raz na build"
     * trzyma `czyMoznaPrzeladowac`, bo pętla przeładowań byłaby gorsza od zasłony.
     */
    if (czyBladWczytaniaModulu(error) && czyMoznaPrzeladowac()) {
      console.warn(`[ErrorBoundary${tag}] brak kawałka aplikacji — przeładowuję raz po wdrożeniu`);
      void przeladujPoBledzieModulu(error);
    }
  }

  componentDidUpdate(prev: Props) {
    if (this.state.hasError && prev.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}
